import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ownerApi, apiErrorText } from "@/lib/ownerApi";

const EMPTY = { current: "", next: "", confirm: "" };

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };
  const close = (v) => { if (!v) { setForm(EMPTY); setError(""); setShow(false); } onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return setError("Please use at least 8 characters.");
    if (form.next !== form.confirm) return setError("The two new passwords don't match.");
    setSaving(true);
    try {
      await ownerApi.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed. Use your new password next time you sign in.");
      close(false);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full rounded-lg bg-[rgba(245,240,232,0.06)] border border-[rgba(197,165,90,0.25)] px-3 py-2.5 text-[15px] text-[#F5F0E8] placeholder:text-[#A0988E]/60 focus:outline-none focus:border-[#D4AF37]";
  const type = show ? "text" : "password";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="change-password-dialog" className="bg-[#14120F] border-[rgba(212,175,55,0.3)] text-[#F5F0E8] max-w-[min(420px,calc(100vw-2rem))] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="serif text-2xl font-light flex items-center gap-2"><KeyRound size={18} className="text-[#D4AF37]" /> Change password</DialogTitle>
          <DialogDescription className="text-[#A0988E]">
            Pick something you&apos;ll remember. Other devices signed in will be signed out.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-1">
          <label className="block text-xs text-[#A0988E]">Current password
            <input data-testid="current-password-input" type={type} value={form.current} onChange={set("current")} autoComplete="current-password" required className={`${field} mt-1`} />
          </label>
          <label className="block text-xs text-[#A0988E]">New password (at least 8 characters)
            <input data-testid="new-password-input" type={type} value={form.next} onChange={set("next")} autoComplete="new-password" required className={`${field} mt-1`} />
          </label>
          <label className="block text-xs text-[#A0988E]">Type the new password again
            <input data-testid="confirm-password-input" type={type} value={form.confirm} onChange={set("confirm")} autoComplete="new-password" required className={`${field} mt-1`} />
          </label>
          <button type="button" data-testid="toggle-show-passwords" onClick={() => setShow((s) => !s)} className="text-xs text-[#A0988E] hover:text-[#F5F0E8] inline-flex items-center gap-1.5">
            {show ? <EyeOff size={13} /> : <Eye size={13} />} {show ? "Hide passwords" : "Show passwords"}
          </button>
          {error && <p data-testid="change-password-error" className="text-sm text-[#E6B89C]">{error}</p>}
          <button type="submit" data-testid="change-password-submit" disabled={saving} className="btn-primary w-full justify-center disabled:opacity-60">
            {saving && <Loader2 size={15} className="animate-spin" />} Save new password
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
